import { useState, useEffect, useCallback, createContext, useContext } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { canAccess, getRequiredPlan, planLabel } from "../lib/planFeatures";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Lock, Crown, Sparkle, ArrowRight } from "@phosphor-icons/react";

/**
 * Subscription state context — fetches /api/billing/subscription once and shares it.
 */
const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/billing/subscription");
      setSubscription(data);
    } catch {
      setSubscription({ plan: "free", status: "free" });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchSubscription(); }, [fetchSubscription]);

  return (
    <SubscriptionContext.Provider value={{ subscription, loading, refresh: fetchSubscription }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) return { subscription: null, loading: false, refresh: () => {} };
  return ctx;
}

/** Convenience hook: returns true if user can access a given feature key. */
export function useFeature(featureKey) {
  const { subscription } = useSubscription();
  if (!subscription) return false;
  return canAccess(subscription.plan, featureKey, {
    isTrialing: subscription.status === "trialing",
    isCreator: !!subscription.creator_account,
  });
}

/**
 * <PlanGate feature="accounting">{children}</PlanGate>
 * Renders children if the user can access; otherwise shows an upgrade card.
 */
export default function PlanGate({ feature, children, fallback }) {
  const { subscription, loading } = useSubscription();
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="w-6 h-6 border-2 border-stone-300 border-t-[#2F4F4F] rounded-full animate-spin mx-auto" />
      </div>
    );
  }
  if (!subscription) return null;
  const allowed = canAccess(subscription.plan, feature, {
    isTrialing: subscription.status === "trialing",
    isCreator: !!subscription.creator_account,
  });
  if (allowed) return children;
  if (fallback) return fallback;
  return <UpgradeCard feature={feature} subscription={subscription} />;
}

function UpgradeCard({ feature, subscription }) {
  const required = getRequiredPlan(feature);
  const featureName = feature.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  const Icon = required === "enterprise" ? Crown : Sparkle;
  const color = required === "enterprise" ? "#3D405B" : "#2F4F4F";

  return (
    <div className="p-6 sm:p-8 max-w-3xl mx-auto" data-testid={`upgrade-card-${feature}`}>
      <div className="bg-white rounded-2xl border border-stone-200 p-8 sm:p-12 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: "radial-gradient(circle at 50% 0%, " + color + " 0%, transparent 70%)"
        }} />
        <div className="relative">
          <div className="w-16 h-16 mx-auto mb-5 rounded-2xl flex items-center justify-center" style={{ backgroundColor: color + "15" }}>
            <Lock size={28} weight="duotone" style={{ color }} />
          </div>
          <Badge className="mb-4 bg-amber-100 text-amber-800 border border-amber-200 text-[10px] uppercase tracking-widest font-semibold">
            <Icon size={11} weight="fill" className="mr-1.5" /> {planLabel(required)} feature
          </Badge>
          <h2 className="text-2xl sm:text-3xl font-normal mb-3 leading-tight" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif", letterSpacing: "-0.01em" }}>
            <span className="italic" style={{ color }}>{featureName}</span> is a {planLabel(required)} feature.
          </h2>
          <p className="text-stone-600 text-sm sm:text-base mb-6 max-w-md mx-auto leading-relaxed">
            You're currently on <strong className="font-semibold">{planLabel(subscription.plan)}</strong>.
            Upgrade to unlock {featureName.toLowerCase()} along with everything else included in {planLabel(required)}.
          </p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <Link to="/billing" data-testid={`upgrade-cta-${feature}`}>
              <Button className="rounded-full px-6 h-11 font-medium" style={{ backgroundColor: color, color: "#FFFFFF" }}>
                See plans <ArrowRight size={14} className="ml-2" weight="bold" />
              </Button>
            </Link>
            <Link to="/" className="text-xs uppercase tracking-widest text-stone-500 hover:text-stone-800 px-3 py-2">
              Back to floor
            </Link>
          </div>
          {subscription.status === "free" && (
            <p className="text-[11px] text-stone-400 mt-5">
              Tip: every plan starts with a 14-day free trial — full access to everything.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
